import * as React from 'react';
import styled, { css } from 'styled-components';

import Flex from '@components/layout/flex';
import Typography from '@components/basics/typography';
import Flag from '@components/basics/flag';
import { countryCodes } from './country-codes';
import CompletedIcon from '@components/basics/icons/achievements-icons/completed';

export interface DropdownItemProps {
  className?: string;
  children?: any;
  decoration?: React.ReactElement;
  rightDecoration?: React.ReactElement;
  helpText?: React.ReactNode;
  disabled?: boolean;
  hasActive?: boolean;
  active?: boolean;
  value?: any;
  onClick?: () => void;
  capitaliseChildren?: boolean;
  isDark?: boolean;
}

const DropdownItem: React.FC<DropdownItemProps> = (props) => {
  const formateEmail = (email: string) => {
    if (email !== '' && email) {
      let index = email.indexOf('@');
      let post = email.slice(index, email.length);
      let pre = email.slice(0, 3);
      return pre + '***' + post;
    }
  };
  const getCountryCode = (name: string) => {
    let code = '';
    if (name) {
      Object.keys(countryCodes).map(function (key: string) {
        let country = (countryCodes as any)[key];
        if (name.toLowerCase() === country.name.toLowerCase()) {
          code = country.iso2;
        }
        return key;
      });
    }
    return code;
  };
  return (
    <Flex
      className={`${props.className} ${props.isDark ? 'dark' : ''}`}
      alignItems="center"
      onClick={() =>
        props.onClick
          ? props?.value?.alreadyExists
            ? null
            : props.onClick()
          : null
      }
    >
      {props.decoration ? (
        <div className="decoration-item">{props.decoration}</div>
      ) : null}
      <Flex
        flexDirection="row"
        className="dropdown-item-wrapper"
        alignItems="center"
      >
        <Flex flexDirection="column" className="dropdown-item-wrapper">
          <Flex
            flexDirection="row"
            className={`dropdown-item-wrapper w-100 ${
              props.rightDecoration ? 'with-right-decoration' : ''
            }`}
          >
            <Typography className="dropdown-item-text" weight="700">
              {props.children}
            </Typography>
            {props.rightDecoration ? (
              <div className="right-decoration-item">
                {props.rightDecoration}
              </div>
            ) : null}
            {props?.value?.country && (
              <Flag
                className="dropdown-item-flag ml-15"
                rectangularFlag
                countryCode={getCountryCode(props?.value?.country?.name)}
              />
            )}
          </Flex>
          {props.helpText ? (
            <Typography weight="700" type="small" opacity=".5">
              {props.helpText}
            </Typography>
          ) : null}
          {props?.value && (
            <Flex
              flexDirection="row"
              justifyContent="space-between"
              className="dropdown-item-wrapper w-100"
            >
              <Typography type="small" opacity=".5">
                {formateEmail(props?.value?.email)}
              </Typography>
            </Flex>
          )}
        </Flex>
        {props?.value?.alreadyExists && (
          <CompletedIcon className="icon check" noAnimation gray />
        )}
      </Flex>
    </Flex>
  );
};

export default styled(DropdownItem)`
  position: relative;
  padding-block-end: 8px;
  flex-grow: 0;
  cursor: pointer;
  width: 100%;
  background-color: transparent;
  padding: 10px;
  isolation: isolate;
  opacity: ${(props) => (props?.value?.alreadyExists ? '0.6' : '1')};

  &:before {
    border-radius: 4px;
    z-index: 0;
    content: '';
    position: absolute;
    inset: 0;
    background-color: transparent;
    transition: background-color
      ${({ theme }) =>
        `${theme.transitions.duration} ${theme.transitions.type}`};
  }

  &:hover {
    &:before {
      background-color: ${({ theme }) => theme.colors.inputBackgroundAlternate};
    }
  }

  &.dark {
    &:hover {
      &:before {
        background-color: ${({ theme }) => theme.colors.darkDropdownHoverBG};
      }
    }
  }

  .dropdown-item-wrapper {
    z-index: 1;
    width: ${(props) =>
      props.decoration ? css`calc(100% - (32px + 12px))` : '100%'};
  }

  .with-right-decoration {
    display: flex;
    justify-content: space-between;
  }

  .disabled {
    color: ${({ theme }) => theme.colors.tertiary};
  }

  .ml-15 {
    margin-left: 15px;
  }

  .dropdown-item-text {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    opacity: 1;
  }

  .help-text {
    color: ${({ theme }) => theme.colors.tertiary};
  }

  &.has-active {
    .dropdown-item-text {
      color: ${({ theme }) => theme.colors.tertiary};
      transition: color
        ${({ theme }) =>
          `${theme.transitions.duration} ${theme.transitions.type}`};
    }

    &.active,
    &:hover {
      background-color: transparent;

      .dropdown-item-text {
        color: ${({ theme }) => theme.colors.primary};
      }
    }
  }

  ${(props) =>
    props.decoration
      ? css`
          .decoration-item {
            margin-right: 12px;
          }
        `
      : null}
  ${(props) =>
    props.rightDecoration
      ? css`
          .right-decoration-item {
            margin-left: 12px;
          }
        `
      : null}

  ${(props) =>
    props.capitaliseChildren
      ? css`
          .dropdown-item-text {
            text-transform: capitalize;
          }
        `
      : null}
`;
